import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CLabel,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";

import Errors, { getFieldErrors } from "../../models/errors";
import { getList } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { warningAlert } from "../utils/messages";

import {
  UniqueListItem,
  newUniqueListItem,
  uniqueListToListItems,
} from "../../models/unique-list-item";
import BarcodeItemForm from "./BarcodeItemForm";
import {
  BarcodeData,
  BarcodeFormItem,
  convertToBarcodeData,
} from "../../utils/barcode-print";
import BarcodeConfirmModal from "./BarcodeConfirmModal";
import AssetsSelect from "../asset/AssetsSelect";
import Asset from "../../models/asset";
import Document from "../../models/document";
import jsPDF from "jspdf";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import DocumentCategory from "../../models/document-category";
import DocumentSubategory from "../../models/document-subcategory";
import DocumentCategoriesSelect from "../document-category/DocumentCategoriesSelect";
import DocumentSubcategoriesSelect from "../document-subcategory/DocumentSubcategoriesSelect";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";

interface BarcodeFormProps {
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const FIXED_COLUMNS = ["Documento"];

type CodeType = "barcode" | "qr";

const BarcodeForm: React.FC<BarcodeFormProps> = ({
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);
  const [tableColumns, setTableColumns] = useState<string[]>(FIXED_COLUMNS);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<readonly Asset[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<
    readonly DocumentCategory[]
  >([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState<
    readonly DocumentSubategory[]
  >([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [codeType, setCodeType] = useState<CodeType>("barcode");

  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [items, setItems] = useState<UniqueListItem<BarcodeFormItem>[]>([]);

  const calculateTotalAmount = (
    tableItems: UniqueListItem<BarcodeFormItem>[]
  ): number => {
    return tableItems.length;
  };

  const fetchDocuments = async (
    assets: readonly Asset[],
    categories: readonly DocumentCategory[],
    subCategories: readonly DocumentSubategory[]
  ) => {
    if (assets.length === 0) {
      setItems([]);
      generateTableHeaders([]);
      setTotalAmount(0);
      return;
    }

    setLoadingDocuments(true);
    try {
      const params = new Map<string, string>();

      if (assets.length > 0) {
        params.set("asset_id", assets.map((a) => a.id!.toString()).join(","));
      }
      if (categories.length > 0) {
        params.set(
          "document_category_id",
          categories.map((c) => c.id!.toString()).join(",")
        );
      }
      if (subCategories.length > 0) {
        params.set(
          "document_subcategory_id",
          subCategories.map((s) => s.id!.toString()).join(",")
        );
      }

      const response = await getList<Document>("/documents/", 1000, 0, params);

      if (response.status === SUCCESS) {
        const newItems = (response.data?.items || [])
          .filter((doc) => doc.id !== undefined && doc.name)
          .map((document) => {
            const barcodeItem: BarcodeFormItem = {
              document: document,
              documentId: document.id!,
              barcode: document.barcode || "",
              id: document.id,
              companyId: document.companyId,
              asset: assets.find((a) => a.id === document.assetId),
            };
            return newUniqueListItem<BarcodeFormItem>(barcodeItem);
          });

        setItems(newItems);
        generateTableHeaders(newItems);
        setTotalAmount(calculateTotalAmount(newItems));
      }
    } catch (error) {
      warningAlert("Error al cargar los documentos");
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleAssetsChange = (assets: readonly Asset[]) => {
    setSelectedAssets(assets);
    if (assets.length > 0) {
      fetchDocuments(assets, selectedCategories, selectedSubCategories);
    } else {
      setItems([]);
      generateTableHeaders([]);
      setTotalAmount(0);
    }
  };

  const onCategoriesChange = (categories: readonly DocumentCategory[]) => {
    setSelectedCategories(categories);
    if (selectedAssets.length > 0) {
      fetchDocuments(selectedAssets, categories, selectedSubCategories);
    }
  };

  const onSubcategoriesChange = (
    subCategories: readonly DocumentSubategory[]
  ) => {
    setSelectedSubCategories(subCategories);
    if (selectedAssets.length > 0) {
      fetchDocuments(selectedAssets, selectedCategories, subCategories);
    }
  };

  const onAddItemClick = useCallback(() => {
    const newItems = [...items, newUniqueListItem<BarcodeFormItem>({})];
    setItems(newItems);
    generateTableHeaders(newItems);
  }, [items]);

  const onItemEntryChange = useCallback(
    (formItem: UniqueListItem<BarcodeFormItem>) => {
      let newItems = [...items];
      const index = newItems.findIndex((item) => {
        return item.uuid === formItem.uuid;
      });

      if (index === -1) {
        setItems(newItems);
        return;
      }

      newItems[index] = formItem;
      setTotalAmount(calculateTotalAmount(newItems));
      setItems(newItems);
      generateTableHeaders(newItems);
    },
    [items]
  );

  const onEntryDelete = useCallback(
    (entry: UniqueListItem<BarcodeFormItem>) => {
      let newItems = [...items];
      const index = newItems.findIndex((item) => {
        return item.uuid === entry.uuid;
      });

      if (index === -1) {
        setItems(newItems);
        return;
      }

      newItems.splice(index, 1);
      setTotalAmount(calculateTotalAmount(newItems));
      setItems(newItems);
      generateTableHeaders(newItems);
    },
    [items]
  );

  const generateTableHeaders = (
    transferItems: UniqueListItem<BarcodeFormItem>[]
  ) => {
    const uniqueSizeNames: string[] = [];
    setTableColumns([...FIXED_COLUMNS, ...uniqueSizeNames, "Acciones"]);
  };

  const onSave = async () => {
    setSubmitting(true);
    const formattedItems = convertToBarcodeData(uniqueListToListItems(items));

    if (codeType === "barcode") {
      generateAndPrintBarcodePDF(formattedItems);
    } else {
      await generateAndPrintQRPDF(formattedItems);
    }

    clearForm();
    setSubmitting(false);
  };

  const clearForm = () => {
    setItems([]);
    setSelectedAssets([]);
    setSelectedCategories([]);
    setSelectedSubCategories([]);
    generateTableHeaders([]);
    setTotalAmount(0);
  };

  const onConfirmSuccess = async () => {
    setSubmitting(true);
    await onSave();
    setShowConfirmModal(false);
    onSuccess();
  };

  const onConfirmCancel = () => {
    setShowConfirmModal(false);
  };

  const onConfirmClick = () => {
    setShowConfirmModal(true);
  };

  const generateAndPrintBarcodePDF = (data: BarcodeData[]) => {
    const customWidth = 56;
    const customHeight = 124;
    const barcodeHeight = 12;
    const margin = 5;
    const stickersPerPage = 4;
    const stickerHeight =
      (customHeight - (stickersPerPage + 1) * margin) / stickersPerPage;
    const offsetY = 2;
    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: [customWidth, customHeight],
    });

    const truncateText = (text: string, maxWidth: number) => {
      let truncatedText = text;
      const ellipsis = "...";
      while (
        pdf.getTextWidth(truncatedText + ellipsis) > maxWidth &&
        truncatedText.length > 0
      ) {
        truncatedText = truncatedText.slice(0, -1);
      }
      return truncatedText + ellipsis;
    };

    let currentStickerCount = 0;
    let currentPage = 1;
    let y = margin;

    pdf.setFontSize(6);
    pdf.setFontSize(16);
    data.forEach(({ code, label, assetName }) => {
      if (currentStickerCount === stickersPerPage) {
        pdf.addPage();
        currentStickerCount = 0;
        y = margin;
        currentPage = currentPage + 1;
      }

      const x = margin;
      const canvas = document.createElement("canvas");
      const isNumeric = /^\d+$/.test(code);
      const marginBarcode = isNumeric ? 50 : 5;

      JsBarcode(canvas, code, {
        format: "CODE128",
        displayValue: true,
        fontSize: 24,
        marginTop: 0,
        marginBottom: 0,
        marginLeft: marginBarcode,
        marginRight: marginBarcode,
      });

      const imgData = canvas.toDataURL("image/png");
      const textWidth = pdf.getTextWidth(code);
      const textX = x + (customWidth - 2 * margin - textWidth) / 2;

      pdf.addImage(
        imgData,
        "PNG",
        textX,
        y + offsetY + (stickerHeight - barcodeHeight) / 2,
        textWidth,
        barcodeHeight
      );
      pdf.setFontSize(6);
      pdf.setFontSize(16);

      if (assetName) {
        pdf.setFontSize(6);
        const labelWidth = pdf.getTextWidth(assetName);
        if (labelWidth > textWidth) {
          const labelTruncated = truncateText(assetName, textWidth);
          const textX = x + (customWidth - 2 * margin - textWidth) / 2;
          pdf.text(
            labelTruncated,
            textX,
            y + offsetY + (stickerHeight - barcodeHeight) / 2 - 2
          );
        } else {
          const textX = x + (customWidth - 2 * margin - labelWidth) / 2;
          pdf.text(
            assetName,
            textX,
            y + offsetY + (stickerHeight - barcodeHeight) / 2 - 2
          );
        }
      }

      if (label) {
        pdf.setFontSize(6);
        const labelWidth = pdf.getTextWidth(label);
        if (labelWidth > textWidth) {
          const labelTruncated = truncateText(label, textWidth);
          const textX = x + (customWidth - 2 * margin - textWidth) / 2;
          pdf.text(
            labelTruncated,
            textX,
            y + offsetY * 2 + (stickerHeight - barcodeHeight) / 2 - 2
          );
        } else {
          const textX = x + (customWidth - 2 * margin - labelWidth) / 2;
          pdf.text(
            label,
            textX,
            y + offsetY * 2 + (stickerHeight - barcodeHeight) / 2 - 2
          );
        }
        pdf.setFontSize(16);
      }

      y += stickerHeight + margin;
      currentStickerCount++;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    });

    pdf.autoPrint();
    const blob = pdf.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const generateAndPrintQRPDF = async (data: BarcodeData[]) => {
    const customWidth = 56;
    const customHeight = 124;
    const qrSize = 20; // Tamaño del QR en mm
    const margin = 5;
    const stickersPerPage = 3;
    const stickerHeight =
      (customHeight - (stickersPerPage + 1) * margin) / stickersPerPage;
    const offsetY = 2;
    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: [customWidth, customHeight],
    });

    const truncateText = (text: string, maxWidth: number) => {
      let truncatedText = text;
      const ellipsis = "...";
      while (
        pdf.getTextWidth(truncatedText + ellipsis) > maxWidth &&
        truncatedText.length > 0
      ) {
        truncatedText = truncatedText.slice(0, -1);
      }
      return truncatedText + ellipsis;
    };

    let currentStickerCount = 0;
    let y = margin;

    for (const { code, label, assetName } of data) {
      if (currentStickerCount === stickersPerPage) {
        pdf.addPage();
        currentStickerCount = 0;
        y = margin;
      }

      const x = margin;

      try {
        // Generar QR code
        const qrDataUrl = await QRCode.toDataURL(code, {
          width: 200,
          margin: 1,
          errorCorrectionLevel: "M",
        });

        // Centrar el QR
        const qrX = x + (customWidth - 2 * margin - qrSize) / 2;
        const qrY = y + offsetY + (stickerHeight - qrSize) / 2;

        pdf.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

        // Texto del asset arriba del QR
        if (assetName) {
          pdf.setFontSize(6);
          const labelWidth = pdf.getTextWidth(assetName);
          const maxWidth = customWidth - 2 * margin;

          if (labelWidth > maxWidth) {
            const labelTruncated = truncateText(assetName, maxWidth);
            const textX =
              x +
              (customWidth - 2 * margin - pdf.getTextWidth(labelTruncated)) / 2;
            pdf.text(labelTruncated, textX, qrY - 2);
          } else {
            const textX = x + (customWidth - 2 * margin - labelWidth) / 2;
            pdf.text(assetName, textX, qrY - 2);
          }
        }

        // Texto del documento debajo del QR
        if (label) {
          pdf.setFontSize(6);
          const labelWidth = pdf.getTextWidth(label);
          const maxWidth = customWidth - 2 * margin;

          if (labelWidth > maxWidth) {
            const labelTruncated = truncateText(label, maxWidth);
            const textX =
              x +
              (customWidth - 2 * margin - pdf.getTextWidth(labelTruncated)) / 2;
            pdf.text(labelTruncated, textX, qrY + qrSize + 3);
          } else {
            const textX = x + (customWidth - 2 * margin - labelWidth) / 2;
            pdf.text(label, textX, qrY + qrSize + 3);
          }
        }

        // Texto del código debajo de todo
        pdf.setFontSize(5);
        const codeWidth = pdf.getTextWidth(code);
        const codeX = x + (customWidth - 2 * margin - codeWidth) / 2;
        pdf.text(code, codeX, qrY + qrSize + 6);
      } catch (error) {
        console.error("Error generando QR:", error);
      }

      y += stickerHeight + margin;
      currentStickerCount++;
    }

    pdf.autoPrint();
    const blob = pdf.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
  }, [initialErrors]);

  const barcodeItemsErrors = getFieldErrors("items", errors) as Errors[];

  return (
    <>
      <fieldset disabled={submitting || loadingDocuments}>
        {/* Selector de tipo de código */}
        <CFormGroup>
          <CRow>
            <CCol md={12}>
              <CLabel>Tipo de Código:</CLabel>
              <div className="d-flex align-items-center gap-4">
                <div className="d-flex align-items-center gap-2">
                  <AnimatedCheckbox
                    value={codeType === "barcode"}
                    onChange={(checked) => {
                      if (checked) {
                        setCodeType("barcode");
                      }
                    }}
                    disabled={submitting || loadingDocuments}
                  />
                  <span
                    style={{ fontWeight: codeType === "barcode" ? 600 : 400 }}
                  >
                    Código de Barras
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <AnimatedCheckbox
                    value={codeType === "qr"}
                    onChange={(checked) => {
                      if (checked) {
                        setCodeType("qr");
                      }
                    }}
                    disabled={submitting || loadingDocuments}
                  />
                  <span style={{ fontWeight: codeType === "qr" ? 600 : 400 }}>
                    Código QR
                  </span>
                </div>
              </div>
            </CCol>
          </CRow>
        </CFormGroup>

        {/* Assets Selection */}
        <CFormGroup>
          <CRow>
            <CCol md={12}>
              <CLabel>Seleccionar Productores:</CLabel>
              <AssetsSelect
                value={selectedAssets}
                onChange={handleAssetsChange}
                isDisabled={submitting || loadingDocuments}
              />
              {loadingDocuments && (
                <small className="text-muted">
                  Cargando documentos de los productores seleccionados...
                </small>
              )}
            </CCol>
          </CRow>
        </CFormGroup>

        {/* Category and sub category Selection */}
        <CFormGroup>
          <CRow>
            <CCol md={6}>
              <CLabel>Seleccionar Categorías:</CLabel>
              <DocumentCategoriesSelect
                value={selectedCategories}
                onChange={onCategoriesChange}
                isDisabled={submitting || loadingDocuments}
              />
            </CCol>
            <CCol md={6}>
              <CLabel>Seleccionar Subcategorías:</CLabel>
              <DocumentSubcategoriesSelect
                value={selectedSubCategories}
                onChange={onSubcategoriesChange}
                isDisabled={submitting || loadingDocuments}
              />
            </CCol>
          </CRow>
        </CFormGroup>

        <CFormGroup>
          <CRow>
            <CCol md={12}>
              <h5>
                Agregue los documentos y la cantidad de Códigos{" "}
                {codeType === "barcode" ? "de barra" : "QR"} que quiere generar
                para cada tipo
              </h5>
            </CCol>
          </CRow>
          <CRow>
            <CCol md={12}>
              <div className="table-responsive">
                <table className="table table-striped table-bordered table-fixed">
                  <thead>
                    <tr>
                      {tableColumns.map((title, ix) => {
                        return (
                          <th
                            className="text-center"
                            key={ix}
                            style={{
                              verticalAlign: "middle",
                              overflow: "hidden",
                            }}
                          >
                            <div className="d-inline">{title}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((document, ix) => {
                      return (
                        <BarcodeItemForm
                          disabled={submitting || loadingDocuments}
                          key={document.uuid}
                          value={document.item.document}
                          sizesColumns={tableColumns}
                          initialValue={document.item.document}
                          onDelete={() => onEntryDelete(document)}
                          onChange={(item) =>
                            onItemEntryChange({
                              uuid: document.uuid,
                              item: item,
                            })
                          }
                          errors={
                            ix < barcodeItemsErrors.length
                              ? barcodeItemsErrors[ix]
                              : {}
                          }
                        />
                      );
                    })}
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={tableColumns.length}>
                          {selectedAssets.length === 0
                            ? "Seleccione productores para cargar sus documentos automáticamente, o agregue nuevas entradas con el botón + de la derecha"
                            : "Agregue nuevas entradas con el botón + de la derecha"}
                        </td>
                      </tr>
                    ) : (
                      <></>
                    )}
                  </tbody>
                </table>
                <br />
                <br />
              </div>
            </CCol>
          </CRow>
          <CRow className="mb-1">
            <CCol>
              <CButton
                className="btn btn-primary float-right"
                onClick={onAddItemClick}
                disabled={submitting || loadingDocuments}
              >
                <i className="fa fa-plus" />
              </CButton>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="submit" color="primary" onClick={onConfirmClick}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Cargando..." : "Imprimir"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
      <BarcodeConfirmModal
        show={showConfirmModal}
        totalAmount={totalAmount}
        onCancel={onConfirmCancel}
        onSuccess={onConfirmSuccess}
      />
    </>
  );
};

export default BarcodeForm;
